import React, { useState } from "react";
import { links } from "./links";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Collapse,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { MetaState, UserState } from "../../types/types";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import { LuDot } from "react-icons/lu";

const LinksDrawer = ({ showLinkIconsOnly = false }: { showLinkIconsOnly?: boolean }) => {
  return (
    <List sx={{ px: 1.5 }}>
      {links.map((link, index) => {
        if (showLinkIconsOnly && link.children) {
          return (
            <React.Fragment key={link.path + index}>
              {link.children.map((childLink: any, childIndex: number) => (
                <SidebarLink
                  key={childLink.path + childIndex}
                  link={childLink}
                  showLinkIconsOnly={showLinkIconsOnly}
                />
              ))}
            </React.Fragment>
          );
        }
        return (
          <SidebarLink
            key={link.path + index}
            link={link}
            showLinkIconsOnly={showLinkIconsOnly}
          />
        );
      })}
    </List>
  );
};

export default LinksDrawer;

const SidebarLink = ({
  link,
  showLinkIconsOnly,
  nested = false,
}: {
  link: any;
  showLinkIconsOnly: boolean;
  nested?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const userData = useSelector((state: UserState) => state.user.userData);
  const { meta } = useSelector((state: MetaState) => state.meta);

  const getRoleId = (roleName: string) => {
    for (const i in meta?.roles ?? {}) {
      if (meta.roles[i] === roleName) return i;
    }
  };

  const userRoles: number[] = userData.roles ?? [];
  const allowedToRolesId = link?.allowedTo?.map((item: any) => getRoleId(item));
  const isAllowed = userRoles.some((role) => allowedToRolesId?.includes(role.toString()));

  if (!isAllowed) return null;
  if (showLinkIconsOnly && link.children) return null;

  // Active detection: match pathname (prefix, except root exact), plus every
  // query param the link itself specifies (e.g. ?type=customer) must match
  // the current URL — extra params like pagination are ignored, but this is
  // what keeps sibling links that differ only by query string (Users,
  // Categories) from all highlighting together.
  const [linkPathname, linkQueryString] = link.path.split("?");
  const pathMatches = linkPathname === "/"
    ? location.pathname === "/"
    : location.pathname === linkPathname || location.pathname.startsWith(linkPathname + "/");

  const linkParams = linkQueryString ? new URLSearchParams(linkQueryString) : null;
  const locationParams = new URLSearchParams(location.search);
  const queryMatches = !linkParams
    || Array.from(linkParams.entries()).every(([key, value]) => locationParams.get(key) === value);

  const isActive = pathMatches && queryMatches;

  const itemSx = {
    borderRadius: "12px",
    mb: 0.5,
    px: showLinkIconsOnly ? 1 : 1.5,
    py: 0.75,
    transition: "background 150ms",
    ...(isActive
      ? {
          bgcolor: (theme: any) => alpha(theme.palette.primary.main, 0.12),
          "&:hover": { bgcolor: (theme: any) => alpha(theme.palette.primary.main, 0.18) },
        }
      : {
          "&:hover": { bgcolor: "action.hover" },
        }),
  };

  const iconColor = isActive ? "primary.main" : "text.secondary";
  const textColor = isActive ? "primary.main" : "text.primary";

  return (
    <>
      <Tooltip title={showLinkIconsOnly ? link.title : ""} placement="left">
        <ListItem disablePadding sx={{ mb: 0 }}>
          {link.children ? (
            <ListItemButton onClick={() => setOpen(!open)} sx={itemSx}>
              <ListItemIcon
                sx={{
                  fontSize: nested ? "16px" : "20px",
                  color: iconColor,
                  minWidth: showLinkIconsOnly ? "24px" : "36px",
                  justifyContent: "center",
                }}
              >
                {<link.icon />}
              </ListItemIcon>
              {!showLinkIconsOnly && (
                <>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={isActive ? 700 : 500} color={textColor}>
                        {link.title}
                      </Typography>
                    }
                  />
                  {open
                    ? <MdKeyboardArrowUp size={18} color={isActive ? "var(--mui-palette-primary-main)" : "var(--mui-palette-text-secondary)"} />
                    : <MdKeyboardArrowDown size={18} color={isActive ? "var(--mui-palette-primary-main)" : "var(--mui-palette-text-secondary)"} />
                  }
                </>
              )}
            </ListItemButton>
          ) : (
            <ListItemButton component={Link} to={link.path} sx={itemSx}>
              <ListItemIcon
                sx={{
                  fontSize: nested ? "16px" : "20px",
                  color: iconColor,
                  minWidth: showLinkIconsOnly ? "24px" : "36px",
                  justifyContent: "center",
                }}
              >
                {nested ? <LuDot /> : <link.icon />}
              </ListItemIcon>
              {!showLinkIconsOnly && (
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={isActive ? 700 : 500} color={textColor}>
                      {link.title}
                    </Typography>
                  }
                />
              )}
            </ListItemButton>
          )}
        </ListItem>
      </Tooltip>

      {link.children && !showLinkIconsOnly && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List disablePadding sx={{ pl: 1.5, mb: 0.5 }}>
            {link.children.map((childLink: any, index: number) => (
              <SidebarLink
                key={childLink.path + index}
                link={childLink}
                showLinkIconsOnly={showLinkIconsOnly}
                nested
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};
